import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconCheckCircle, IconClose, IconEdit, IconTrash } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import ClientFarmSearch from '../../clients/forms/search';
import { useBirthsList, useUpdateBirth } from '../../births/contexts/births';

import './styles/worklist.css';

// Tab "Tickets de preñeces" (admin-births.md · registro de la cría, 0060). Las crías
// ya nacidas (post-parto) reciben el **ticket de registro** (`registration_ticket`),
// distinto del ticket del cruce (`genealogy_ticket`, derivado). Se asigna vía PATCH
// del birth. Sin filtro server por estado de registro → se filtra en cliente.
const today = () => new Date().toLocaleDateString('en-CA');

const HAS_TICKET = [
    { value: 'false', label: 'Por asignar' },
    { value: 'true',  label: 'Con ticket' },
    { value: '',      label: 'Todos' },
];

const REG_STATUS = {
    registered: { label: 'Registrada', tone: 'good' },
    ticketed:   { label: 'Con ticket', tone: 'good' },
    pending:    { label: 'Pendiente',  tone: 'open' },
    lost:       { label: 'Vencida',    tone: 'bad' },
};

function BirthRow({ birth }) {
    const reg = birth.registration || {};
    const has = !!reg.registration_ticket;
    const { mutateAsync: update, isPending } = useUpdateBirth(birth.id);
    const [editing, setEditing] = useState(false);
    const [ticket, setTicket] = useState(reg.registration_ticket || '');
    const [date, setDate] = useState(reg.registration_ticket_at || today());
    const open = !has || editing;

    const save = async () => {
        const id = ticket.trim();
        if (!id) { toast.error('Ingresá el ticket de registro'); return; }
        try {
            await update({ registration_ticket: id, ...(date ? { registration_ticket_at: date } : {}) });
            toast.success(has ? 'Ticket actualizado' : 'Ticket asignado');
            setEditing(false);
        } catch (err) {
            if (err?.status !== 403) toast.error(err?.message || 'No se pudo guardar el ticket');
        }
    };
    const del = async () => {
        try {
            await update({ registration_ticket: null, registration_ticket_at: null });
            toast.success('Ticket quitado');
            setEditing(false); setTicket(''); setDate(today());
        } catch (err) {
            if (err?.status !== 403) toast.error(err?.message || 'No se pudo quitar el ticket');
        }
    };

    const st = REG_STATUS[reg.status];
    const calf = birth.calf || {};
    return (
        <article className="aso-birth">
            <div className="aso-birth__main">
                <div className="aso-birth__head">
                    <span className="aso-birth__calf">{calf.name || calf.number || `#${calf.id}`}</span>
                    {calf.number && calf.name && <span className="aso-birth__num">{calf.number}</span>}
                    {st && <span className={`aso-preg aso-preg--${st.tone}`}>{st.label}</span>}
                </div>
                <div className="aso-birth__meta">
                    <span>{birth.farm?.name || 'Sin finca'}</span>
                    <span className="aso-birth__sep">·</span>
                    <span>Parto {formatDateOnly(birth.date) || '—'}</span>
                    <span className="aso-birth__sep">·</span>
                    <span>Cruce: <strong>{reg.genealogy_ticket || '—'}</strong></span>
                    {reg.registration && <><span className="aso-birth__sep">·</span><span>Registro: <strong>{reg.registration}</strong></span></>}
                    {reg.deadline && !has && <><span className="aso-birth__sep">·</span><span className="aso-birth__deadline">vence {formatDateOnly(reg.deadline)}</span></>}
                </div>
            </div>

            <div className="aso-cross__ticket">
                {has && !editing && (
                    <div className="aso-ticket">
                        <span className="aso-ticket__badge"><IconCheckCircle /> {reg.registration_ticket}</span>
                        {reg.registration_ticket_at && <span className="aso-ticket__date">Comprado {formatDateOnly(reg.registration_ticket_at)}</span>}
                        <Gate domain="births" action="write">
                            <div className="aso-ticket__actions">
                                <button type="button" className="aso-icon-btn" onClick={() => { setTicket(reg.registration_ticket || ''); setDate(reg.registration_ticket_at || today()); setEditing(true); }} disabled={isPending} aria-label="Editar ticket" title="Editar"><IconEdit /></button>
                                <button type="button" className="aso-icon-btn aso-icon-btn--danger" onClick={del} disabled={isPending} aria-label="Quitar ticket" title="Quitar"><IconTrash /></button>
                            </div>
                        </Gate>
                    </div>
                )}

                {open && (
                    <Gate domain="births" action="write" fallback={<span className="aso-ticket__none">Sin ticket</span>}>
                        <div className="aso-ticket-form">
                            <input type="text" className="aso-ticket-form__id" value={ticket} onChange={(e) => setTicket(e.target.value)} maxLength={80} placeholder="Ticket de registro" disabled={isPending} />
                            <input type="date" className="aso-ticket-form__date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} disabled={isPending} />
                            <div className="aso-ticket-form__actions">
                                {editing && <button type="button" className="aso-btn aso-btn--ghost" onClick={() => setEditing(false)} disabled={isPending}>Cancelar</button>}
                                <button type="button" className="aso-btn aso-btn--primary" onClick={save} disabled={isPending || !ticket.trim()}>
                                    {isPending ? 'Guardando…' : (has ? 'Guardar' : 'Asignar')}
                                </button>
                            </div>
                        </div>
                    </Gate>
                )}
            </div>
        </article>
    );
}

export default function BirthTicketsWorklist() {
    const [hasTicket, setHasTicket] = useState('false'); // filtro cliente (registration_ticket)
    const [farm, setFarm] = useState(null);              // { id, name } finca (server)
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const filters = useMemo(() => {
        const out = { limit: 500, status: 2, sort: '-date' };
        if (farm) out.farm = farm.id;
        if (from) out.from = from;
        if (to) out.to = to;
        return out;
    }, [farm, from, to]);

    const { data, isLoading, isFetching, error } = useBirthsList(filters);
    const births = useMemo(() => data?.items ?? [], [data]);
    const total = data?.pagination?.total ?? births.length;
    const truncated = total > births.length;

    // Filtro por ticket de registro (no hay filtro server) sobre la página traída.
    const view = useMemo(() => births.filter((b) => {
        const has = !!b.registration?.registration_ticket;
        if (hasTicket === 'true') return has;
        if (hasTicket === 'false') return !has;
        return true;
    }), [births, hasTicket]);

    return (
        <div className="aso">
            <header className="aso__header">
                <div className="aso__heading">
                    <h1 className="aso__title">Tickets de preñeces</h1>
                    <span className="aso__count">{view.length}</span>
                </div>
                <p className="aso__subtitle">Crías nacidas: asigná el ticket de registro (después del parto). El ticket del cruce (genealogía) se hereda automáticamente.</p>
            </header>

            <div className="aso__toolbar">
                <div className="aso__seg" role="group" aria-label="Estado del ticket de registro">
                    {HAS_TICKET.map((o) => (
                        <button
                            key={o.value || 'all'}
                            type="button"
                            className={`aso__seg-btn ${hasTicket === o.value ? 'aso__seg-btn--active' : ''}`}
                            onClick={() => setHasTicket(o.value)}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>

                <div className="aso__filter aso__filter--search">
                    <span className="aso__filter-label">Finca</span>
                    {farm ? (
                        <div className="aso-active">
                            <span className="aso-active__label">Finca</span>
                            <span className="aso-active__value">{farm.name}</span>
                            <button type="button" className="aso-active__clear" onClick={() => setFarm(null)} aria-label="Quitar filtro"><IconClose /></button>
                        </div>
                    ) : (
                        <ClientFarmSearch scope="all" farmsOnly placeholder="Finca…" onSelect={(it) => setFarm({ id: it.id, name: it.name })} />
                    )}
                </div>

                <label className="aso__filter">
                    <span className="aso__filter-label">Desde (fecha parto)</span>
                    <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
                </label>
                <label className="aso__filter">
                    <span className="aso__filter-label">Hasta</span>
                    <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
                </label>
            </div>

            {error && <div className="aso__error">{error.status === 403 ? 'No tenés permiso para ver los nacimientos (births:read).' : (error.message || 'No se pudieron cargar los nacimientos')}</div>}
            {truncated && (
                <p className="aso__note">
                    {hasTicket
                        ? `Atención: el filtro se aplica solo sobre los primeros ${births.length} de ${total} nacimientos cargados — pueden quedar crías sin mostrar. Acotá por finca o fecha del parto para no perder ninguna.`
                        : `Mostrando los primeros ${births.length} de ${total} nacimientos. Acotá por finca o fecha del parto.`}
                </p>
            )}

            {isLoading ? (
                <div className="aso__state">Cargando crías…</div>
            ) : !view.length ? (
                <div className="aso__state">{hasTicket === 'false' ? 'No hay crías por asignar ticket de registro.' : 'No hay crías para el filtro.'}</div>
            ) : (
                <div className={`aso__births ${isFetching ? 'aso__list--busy' : ''}`}>
                    {view.map((b) => <BirthRow key={b.id} birth={b} />)}
                </div>
            )}
        </div>
    );
}
